import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useCallback } from 'react';

const GTMTrackingContext = createContext(null);

/**
 * ```<GTMTracker>``` is a contextprovider-component with that exposes the functionality two hooks for usage within it.
 *
 * * useTrackCustomClick - A hook that allows the user to track custom clicks
 *
 * * useTrackVirtualView - A hook that allows the user to track virtual page views.
 *
 *  @param  {string}  mfName - The name of the particular microfrontend implementing the component, for example loginflow.
 *
 */
const GTMTracker = ({ children, mfName }) => (jsx(GTMTrackingContext.Provider, { value: { mfName }, children: children }));

const eventNames$3 = {
    internals: {
        gtmCustomEventName: 'app-shell:gtmCustomEvent'},
};
const __api_internals__ = {
    eventNames: eventNames$3.internals,
};

var UserType;
(function (UserType) {
    UserType[UserType["client"] = 0] = "client";
    UserType[UserType["customer"] = 1] = "customer";
})(UserType || (UserType = {}));

const eventNames = __api_internals__.eventNames;
const gtmCustomEvent = (e) => new CustomEvent(eventNames.gtmCustomEventName, {
    bubbles: true,
    detail: e,
});
/**
 * Internal function - not to be used by any consumer other than appshell
 * Push a custom event to the GTM datalayer
 *
 * @param  {string}  e.event_action - The action of the event, e.g. ```click```.
 * @param  {string}   e.event_item - The item of the event, e.g. ```button```.
 * @param  {customData}   e.event_data - An array of key/value pairs, e.g ```
 * [{key: age, value: 30}, {key: height, value: 189}].```
 * @param  {string=}   mfName - The name of the triggering mf, e.g ```meetings```
 * @param  {boolean}   overrideGtmEnabledBlock - Override the block for sending events
 * to GTM if you do not have a gtm.enabled.MfName feature flag. Only to be used in app-shell.
 *
 */
const internal_trackGtmCustomEvent = (e) => dispatchEvent(gtmCustomEvent(e));

const internal_buildCustomClickEvent = (identifier, options = {}, mfName, overrideGtmEnabledBlock = false) => {
    const event = {
        event_action: 'click',
        event_item: identifier,
        event_data: [],
        mfName: mfName,
        overrideGtmEnabledBlock: overrideGtmEnabledBlock,
    };
    if (options.label) {
        event.event_data.push({ key: 'label', value: options.label });
    }
    if (options.href) {
        event.event_data.push({ key: 'href', value: options.href });
    }
    return event;
};
/**
 * A hook that allows the user to track custom clicks. This can be usedfull when base tracking does not
 * cover the needs.
 *
 * @param  {string}  identifier - The identifier of of the item clicked
 * @param  {string}  options.label - The potential inner text of the item clicked
 * @param  {string}  options.href - The potential href of the item clicked.
 * @returns {function(): void} The trigger function used to send an event to the datalayer in GTM.
 */
const useTrackCustomClick = () => {
    const context = useContext(GTMTrackingContext);
    const trackerFunction = useCallback((identifier, options) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!context && process?.env?.NODE_ENV === 'development') {
            console.warn('In order to use useTrackCustomClick you must wrap your component in `GTMTracker`');
            return;
        }
        const customClickEvent = internal_buildCustomClickEvent(identifier, options, context?.mfName);
        internal_trackGtmCustomEvent(customClickEvent);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    return trackerFunction;
};

/**
 * A hook that allows the user to track virtual page views. An example of a virtual page view
 * could be a microfrontend that handles its own non URL-based "routing" via state to render
 * a set of different steps.
 *
 * @param  {string}  virtualView - The name of the virtual view, e.g activityWizard.
 * @returns {function(): trackVirtualViewTrigger } The trigger function used to send an event to the datalayer in GTM.
 *
 * ___
 * These props are a part of the curried function which is returned from the hook.
 * @param  {string}  virtualSubView - The name of a particular subview within the virtual view, e.g startStep.
 * @returns {function(): void} An event emitted that is handled by the AppShell.
 */
const useTrackVirtualView = (virtualView) => {
    const context = useContext(GTMTrackingContext);
    const trackerFunction = useCallback((virtualSubView) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!context && process?.env?.NODE_ENV === 'development') {
            console.warn('In order to use useTrackVirtualView you must wrap your component in `GTMTracker`');
            return;
        }
        const event = {
            event_action: 'view',
            event_item: virtualView,
            event_data: [],
            mfName: context?.mfName,
        };
        if (virtualSubView) {
            event.event_data.push({ key: 'sub_view_name', value: virtualSubView });
        }
        internal_trackGtmCustomEvent(event);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [virtualView]);
    return trackerFunction;
};

/**
 * A hook that allows the user to track when certain loading events are completed. This can be used to notify GTM that a certain microfrontend was loaded, for example.
 *
 * As this hook can be used at any time, make sure that the event triggered is actually making sense as a "loaded" event and does not run repeatedly on re-renders.
 *
 * @param  {string}  identifier - The identifier of of the loaded thing.
 * @returns {function(): void} The trigger function used to send an event to the datalayer in GTM.
 */
const useTrackLoaded = () => {
    const context = useContext(GTMTrackingContext);
    const trackerFunction = useCallback((identifier) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!context && process?.env?.NODE_ENV === 'development') {
            console.warn('In order to use useTrackLoaded you must wrap your component in `GTMTracker`');
            return;
        }
        const event = {
            event_action: 'loaded',
            event_item: identifier,
            event_data: [],
            mfName: context?.mfName,
        };
        internal_trackGtmCustomEvent(event);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return trackerFunction;
};

const internal = {
    buildCustomClickEvent: internal_buildCustomClickEvent,
};

export { GTMTracker, internal, useTrackCustomClick, useTrackLoaded, useTrackVirtualView };
//# sourceMappingURL=index.esm.js.map
